import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, Calendar, MapPin, Users, Clock, Edit, Trash2, ArrowLeft, User } from 'lucide-react';
import api from '../../services/api';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { useToast } from '../../contexts/ToastContext';

interface GuestRegistration {
  _id: string;
  guestUser?: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    organization?: string;
    customFields?: { [key: string]: string };
  };
  event: {
    _id: string;
    title: string;
    description: string;
    startTime: string;
    endTime: string;
    location?: string;
    maxParticipants?: number;
    currentParticipants?: number;
    communityInviteUrl?: string;
  };
  payment?: {
    _id: string;
    amount: number;
    currency?: string;
    status: string;
    razorpayPaymentId?: string;
    createdAt?: string;
  };
  status: string;
  registrationDate: string;
  notes?: string;
}

const RegistrationSuccess: React.FC = () => {
  const { registrationId } = useParams<{ registrationId: string }>();
  const navigate = useNavigate();
  const { success, error } = useToast();
  const [registration, setRegistration] = useState<GuestRegistration | null>(null);
  const [loading, setLoading] = useState(true);
  const [withdrawing, setWithdrawing] = useState(false);

  useEffect(() => {
    if (registrationId) {
      fetchRegistrationDetails();
    }
  }, [registrationId]);

  const fetchRegistrationDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/registrations/guest/${registrationId}`);
      setRegistration(response.data);
    } catch (err: any) {
      error('Failed to fetch registration details');
      navigate('/events');
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async () => {
    if (!registration) return;
    
    try {
      setWithdrawing(true);
      await api.post(`/registrations/guest/${registrationId}/withdraw`);
      success('Registration withdrawn successfully');
      navigate('/events');
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to withdraw registration');
    } finally {
      setWithdrawing(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!registration) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="text-red-600 mb-4">Registration not found</div>
          <Button onClick={() => navigate('/events')} variant="outline">
            Back to Events
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* Header */}
      <div className="mb-8">
        <Link to="/events" className="flex items-center text-blue-600 hover:text-blue-800 mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Events
        </Link>
        
        <div className="text-center">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Registration Successful!
          </h1>
          <p className="text-gray-600">
            You have successfully registered for {registration.event.title}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Registration Details */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Your Registration Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Only a guest registration carries these; a member's details live
                on their profile. */}
            {registration.guestUser && (
            <>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">First Name</label>
                <p className="text-gray-900">{registration.guestUser.firstName}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Last Name</label>
                <p className="text-gray-900">{registration.guestUser.lastName}</p>
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-500">Email</label>
              <p className="text-gray-900">{registration.guestUser.email}</p>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-500">Phone</label>
              <p className="text-gray-900">{registration.guestUser.phone}</p>
            </div>
            
            {registration.guestUser.organization && (
              <div>
                <label className="text-sm font-medium text-gray-500">Organization</label>
                <p className="text-gray-900">{registration.guestUser.organization}</p>
              </div>
            )}

            {registration.guestUser.customFields && Object.keys(registration.guestUser.customFields).length > 0 && (
              <div>
                <label className="text-sm font-medium text-gray-500">Additional Information</label>
                <div className="space-y-2">
                  {Object.entries(registration.guestUser.customFields).map(([key, value]) => (
                    <div key={key} className="flex justify-between">
                      <span className="text-gray-600">{key}:</span>
                      <span className="text-gray-900">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            </>
            )}

            {registration.notes && (
              <div>
                <label className="text-sm font-medium text-gray-500">Notes</label>
                <p className="text-gray-900">{registration.notes}</p>
              </div>
            )}

            <div className="pt-4 border-t">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Registration Date:</span>
                <span className="text-gray-900">{formatDate(registration.registrationDate)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Status:</span>
                <span className={`font-medium ${
                  registration.status === 'confirmed' ? 'text-green-600' : 
                  registration.status === 'registered' ? 'text-yellow-600' : 
                  'text-gray-600'
                }`}>
                  {registration.status === 'confirmed' ? 'Confirmed' : 
                   registration.status === 'registered' ? 'Pending Approval' : 
                   registration.status}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Event Details */}
        <Card>
          <CardHeader>
            <CardTitle>Event Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="font-semibold text-lg">{registration.event.title}</h3>
              <p className="text-sm text-gray-600 mt-1">{registration.event.description}</p>
            </div>

            <div className="space-y-3">
              <div className="flex items-center text-sm">
                <Calendar className="h-4 w-4 mr-2 text-gray-500" />
                <span>Start: {formatDate(registration.event.startTime)} at {formatTime(registration.event.startTime)}</span>
              </div>
              
              <div className="flex items-center text-sm">
                <Calendar className="h-4 w-4 mr-2 text-gray-500" />
                <span>End: {formatDate(registration.event.endTime)} at {formatTime(registration.event.endTime)}</span>
              </div>

              {registration.event.location && (
                <div className="flex items-center text-sm">
                  <MapPin className="h-4 w-4 mr-2 text-gray-500" />
                  <span>{registration.event.location}</span>
                </div>
              )}

              {registration.event.maxParticipants && (
                <div className="flex items-center text-sm">
                  <Users className="h-4 w-4 mr-2 text-gray-500" />
                  <span>{registration.event.currentParticipants || 0}/{registration.event.maxParticipants} participants</span>
                </div>
              )}
            </div>

            {/* What was paid. The confirmation used to say nothing about money,
                which is the first thing anyone who has just paid looks for. */}
            {registration.payment && (
              <div className="pt-4 border-t">
                <div className="rounded-md border border-green-200 bg-green-50 p-4">
                  <h4 className="mb-2 font-medium text-green-900">Payment received</h4>
                  <div className="space-y-1 text-sm text-green-900">
                    <p>
                      <strong>Amount:</strong> ₹{(registration.payment.amount / 100).toFixed(2)}{' '}
                      {registration.payment.currency || 'INR'}
                    </p>
                    {registration.payment.razorpayPaymentId && (
                      <p className="break-all">
                        <strong>Payment ID:</strong> {registration.payment.razorpayPaymentId}
                      </p>
                    )}
                    <p className="text-green-800">A receipt has been emailed to you.</p>
                  </div>
                  <a
                    href={`/api/payments/receipt/${registration.payment._id}`}
                    className="mt-3 inline-flex items-center rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-green-700"
                  >
                    Download receipt
                  </a>
                </div>
              </div>
            )}

            {/* The group is where the event actually happens. Shown here as well
                as emailed, because this is the screen they are already on. */}
            {registration.event.communityInviteUrl && (
              <div className="pt-4 border-t">
                <div className="rounded-md border border-blue-200 bg-blue-50 p-4">
                  <h4 className="mb-1 font-medium text-blue-900">Join the group</h4>
                  <p className="mb-3 text-sm text-blue-800">
                    Updates, questions and announcements for this event happen here.
                  </p>
                  <a
                    href={registration.event.communityInviteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
                  >
                    Open the group
                  </a>
                </div>
              </div>
            )}

            <div className="pt-4 border-t">
              <div className="bg-green-50 border border-green-200 rounded-md p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <h4 className="font-medium text-green-900">Registration Confirmed</h4>
                </div>
                <p className="text-sm text-green-800">
                  {registration.status === 'confirmed' 
                    ? 'Your registration has been confirmed. You will receive event details via email.'
                    : 'Your registration is pending admin approval. You will be notified once approved.'
                  }
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Action Buttons */}
      <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
        <Button
          onClick={() => navigate('/events')}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          Browse More Events
        </Button>
        
        <Button
          onClick={() => navigate(`/events/${registration.event._id}`)}
          variant="outline"
        >
          View Event Details
        </Button>
        
        <Button
          onClick={handleWithdraw}
          variant="outline"
          className="text-red-600 hover:text-red-700 border-red-600 hover:border-red-700"
          disabled={withdrawing}
        >
          {withdrawing ? (
            <>
              <LoadingSpinner size="sm" />
              <span className="ml-2">Withdrawing...</span>
            </>
          ) : (
            <>
              <Trash2 className="h-4 w-4 mr-2" />
              Withdraw Registration
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default RegistrationSuccess; 